"use client";

import Link from "next/link";
import { ChevronLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import Badge, { type BadgeVariant } from "@/components/ui/Badge";

export interface DetailHeaderProps {
  title: string;
  backHref: string;
  backLabel?: string;
  icon?: React.ReactNode;
  subtitle?: React.ReactNode;
  badge?: { label: string; variant: BadgeVariant };
  actions?: React.ReactNode;
  className?: string;
}

export default function DetailHeader({
  title,
  backHref,
  backLabel,
  icon,
  subtitle,
  badge,
  actions,
  className,
}: DetailHeaderProps) {
  return (
    <div className={cn("flex items-center justify-between", className)}>
      <div className="flex items-center gap-3">
        <Link
          href={backHref}
          className={cn(
            "rounded-lg border border-neon-cyan/15 p-2 text-ash transition-colors hover:bg-neon-cyan/5",
            backLabel && "inline-flex items-center gap-1.5 px-3"
          )}
          aria-label={backLabel ? undefined : "Back"}
        >
          <ChevronLeft className="h-5 w-5" />
          {backLabel && <span className="text-sm font-medium">{backLabel}</span>}
        </Link>
        <div>
          <div className="flex items-center gap-2">
            {icon}
            <h1 className="text-xl font-bold text-foreground sm:text-2xl">{title}</h1>
            {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
          </div>
          {subtitle && <p className="mt-0.5 text-xs text-ash sm:text-sm">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
